import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Box, TextField, Button, FormLabel, Typography, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { convertGenderText } from '../utils/consts';

const EcologySection = ({ onNext, onBack, isFirstSection, formData }) => {
    const everdayLife = (goal) => {
        switch (goal) {
            case "Szukam pierwszej pracy":
                return `Jaki wpływ na Twoje otoczenie ma to, że ${convertGenderText('znalazłeś/aś', formData.zwrot_plec)} pierwszą pracę?`;
            case "Szukam pracy":
                return `Jaki wpływ na Twoje otoczenie ma to, że ${convertGenderText('znalazłeś/aś', formData.zwrot_plec)} pracę?`;
            case "Myślę o znalezieniu innej/nowej pracy":
                return `Jaki wpływ na Twoje otoczenie ma to, że ${convertGenderText('podjąłeś/łaś', formData.zwrot_plec)} decyzję o szukaniu innej pracy?`;
            case "Wracam na rynek pracy":
                return `Jaki wpływ na Twoje otoczenie ma to, że ${convertGenderText('wróciłeś/aś', formData.zwrot_plec)} na rynek pracy?`;
            case "Przekwalifikowuję się":
                return `Jaki wpływ na Twoje otoczenie ma to, że ${convertGenderText('przekwalifikowałeś/aś się', formData.zwrot_plec)}?`;
            default:
                return "";
        }
    }
    const selfImage = (goal) => {
        switch (goal) {
            case "Szukam pierwszej pracy":
                return `Teraz, kiedy ${convertGenderText('znalazłeś/aś', formData.zwrot_plec)} pierwszą pracę, co myślisz o sobie?`;
            case "Szukam pracy":
                return `Teraz, kiedy ${convertGenderText('znalazłeś/aś', formData.zwrot_plec)} pracę, co myślisz o sobie?`;
            case "Myślę o znalezieniu innej/nowej pracy":
                return `Teraz, kiedy ${convertGenderText('podjąłeś/łaś', formData.zwrot_plec)} decyzję o szukaniu innej pracy, co myślisz o sobie?`;
            case "Wracam na rynek pracy":
                return `Teraz, kiedy ${convertGenderText('wróciłeś/aś', formData.zwrot_plec)} na rynek pracy, co myślisz o sobie?`;
            case "Przekwalifikowuję się":
                return `Teraz, kiedy ${convertGenderText('przekwalifikowałeś/aś się', formData.zwrot_plec)}, co myślisz o sobie?`;
            default:
                return "";
        }
    }
    const relationships = (goal) => {
        switch (goal) {
            case "Szukam pierwszej pracy":
                return "Jaki wpływ na relacje z innymi może mieć znalezienie pierwszej pracy?";
            case "Szukam pracy":
                return "Jaki wpływ na relacje z innymi może mieć znalezienie pracy?";
            case "Myślę o znalezieniu innej/nowej pracy":
                return "Jaki wpływ na relacje z innymi może mieć decyzja o szukaniu innej pracy?";
            case "Wracam na rynek pracy":
                return "Jaki wpływ na relacje z innymi może mieć powrót na rynek pracy?";
            case "Przekwalifikowuję się":
                return "Jaki wpływ na relacje z innymi może mieć przekwalifikowanie się?";
            default:
                return "Jak teraz wyglądają Twoje relacje z innymi?";
        }
    }
    // Define your steps. Each step is associated with a key for the final answers.
    const steps = [
        {
            name: 'ekologia_codziennosc',
            label: everdayLife(formData.cel_wybor)
        },
        {
            name: 'ekologia_relacje',
            label: relationships(formData.cel_wybor)
        },
        {
            name: 'ekologia_mysli_o_sobie',
            label: selfImage(formData.cel_wybor)
        },
        {
            name: 'ekologia_samopoczucie',
            label: 'Jak się z tym czujesz?'
        }
    ];

    // currentStep tracks which question we are on.
    const initialStep = (() => {

        // Determine which step to show based on completed data
        if (formData?.ekologia_mysli_o_sobie) return 3;
        if (formData?.ekologia_relacje) return 2;
        if (formData?.ekologia_codziennosc) return 1;
        return 0;
    })();

    const [currentStep, setCurrentStep] = useState(initialStep);

    // answers accumulates each step's answer keyed by the field name.
    const [answers, setAnswers] = useState({
        ekologia_codziennosc: formData?.ekologia_codziennosc || '',
        ekologia_relacje: formData?.ekologia_relacje || '',
        ekologia_mysli_o_sobie: formData?.ekologia_mysli_o_sobie || '',
        ekologia_samopoczucie: formData?.ekologia_samopoczucie || ''
    });

    // We only need one form field for the current answer.
    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: {
            currentAnswer: formData?.[steps[initialStep]?.name] || ''
        }
    });

    // Sync answers with formData when user returns to this section
    useEffect(() => {
        if (formData?.ekologia_codziennosc || formData?.ekologia_relacje ||
            formData?.ekologia_mysli_o_sobie || formData?.ekologia_samopoczucie) {
            setAnswers({
                ekologia_codziennosc: formData?.ekologia_codziennosc || '',
                ekologia_relacje: formData?.ekologia_relacje || '',
                ekologia_mysli_o_sobie: formData?.ekologia_mysli_o_sobie || '',
                ekologia_samopoczucie: formData?.ekologia_samopoczucie || ''
            });
        }

        // eslint-disable-next-line
    }, [formData]);

    // Update form values when currentStep changes
    useEffect(() => {
        const currentFieldName = steps[currentStep]?.name;
        if (currentFieldName) {
            reset({ currentAnswer: answers[currentFieldName] || '' });
        }
        // eslint-disable-next-line
    }, [currentStep, reset, answers]);

    const onSubmitStep = data => {
        const fieldName = steps[currentStep].name;
        const currentAnswer = data.currentAnswer;

        // Save the current answer into our answers state.
        const newAnswers = { ...answers, [fieldName]: currentAnswer };
        setAnswers(newAnswers);

        if (currentStep < steps.length - 1) {
            // Reset the form with the next question's saved answer if it exists
            const nextStep = currentStep + 1;
            const nextFieldName = steps[nextStep].name;
            reset({ currentAnswer: newAnswers[nextFieldName] || '' });
            setCurrentStep(nextStep);
        } else {
            // On the final step, combine answers with the last answer and submit.
            onNext(newAnswers);
        }
    };

    const currentStepData = steps[currentStep];

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmitStep)} sx={{ mt: 3 }}>
            <FormLabel>
                <Typography variant="h6" color="black" gutterBottom>
                    {currentStepData.label}
                </Typography>
            </FormLabel>
            <Controller
                name="currentAnswer"
                control={control}
                rules={{ required: "To pole jest wymagane" }}
                render={({ field }) => (
                    <TextField
                        {...field}
                        fullWidth
                        multiline
                        rows={4}
                        error={!!errors.currentAnswer}
                        helperText={errors.currentAnswer?.message}
                        sx={{ mb: 3 }}
                    />
                )}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
                <IconButton
                    onClick={() => {
                        if (currentStep === 0) {
                            onBack();
                        } else {
                            const prevStep = currentStep - 1;
                            const prevFieldName = steps[prevStep].name;
                            reset({ currentAnswer: answers[prevFieldName] || '' });
                            setCurrentStep(prevStep);
                        }
                    }}
                    disabled={isFirstSection}
                    sx={{
                        border: '1px solid',
                        borderColor: isFirstSection ? 'action.disabled' : 'primary.main',
                        borderRadius: 1
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    endIcon={<ArrowForwardIcon />}
                >
                    Dalej
                </Button>
            </Box>
        </Box>
    );
};

export default EcologySection;
